import axios from "axios";
import { useEffect, useState } from "react";
import { useTheme } from "../guardarToken/themeContext";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from '@mui/icons-material/Add';
import CategoryIcon from '@mui/icons-material/Category';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';

const apiUrlPlatos = process.env.REACT_APP_API_PLATOS || '/api/menus/platos/';
// Determina base API: si es absoluta, respétala; si es relativa usa el mismo origen del frontend (producción)
const apiBaseUrl = apiUrlPlatos.startsWith('http')
    ? apiUrlPlatos.replace(/\/platos\/?$/, '')
    : `${window.location.origin}${apiUrlPlatos.replace(/\/platos\/?$/, '')}`;

const AbmTiposMenu = () => {
    const { isDarkMode } = useTheme();
    const [tipos, setTipos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [form, setForm] = useState({ nombre: '', descripcion: '' });
    const [editingId, setEditingId] = useState(null);
    const [selectedTipo, setSelectedTipo] = useState(null);
    const [platosDelTipo, setPlatosDelTipo] = useState([]);
    const [loadingPlatos, setLoadingPlatos] = useState(false);

    const loadTipos = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await axios.get(`${apiBaseUrl}/tipos-menu`);
            setTipos(res.data);
        } catch (err) {
            setError('No se pudieron cargar los tipos de menú');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadTipos();
    }, []);

    const fetchPlatosByTipo = async (tipo) => {
        setLoadingPlatos(true);
        setSelectedTipo(tipo);
        try {
            // Suponemos que podemos filtrar platos por tipo en el backend
            const res = await axios.get(`${apiBaseUrl}/platos?tipo=${tipo.nombre}`);
            setPlatosDelTipo(res.data);
        } catch (err) {
            console.error('Error cargando platos del tipo', err);
        } finally {
            setLoadingPlatos(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const resetForm = () => {
        setForm({ nombre: '', descripcion: '' });
        setEditingId(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.nombre.trim()) return;
        try {
            if (editingId) {
                await axios.put(`${apiBaseUrl}/tipos-menu/${editingId}`, {
                    nombre: form.nombre.trim(),
                    descripcion: form.descripcion.trim(),
                });
            } else {
                await axios.post(`${apiBaseUrl}/tipos-menu`, {
                    nombre: form.nombre.trim(),
                    descripcion: form.descripcion.trim(),
                });
            }
            await loadTipos();
            resetForm();
        } catch (err) {
            console.error('Error al guardar tipo de menú', err);
            setError('No se pudo guardar el tipo de menú');
        }
    };

    const handleEdit = (tipo) => {
        setEditingId(tipo.id);
        setForm({ nombre: tipo.nombre, descripcion: tipo.descripcion || '' });
    };


    const handleDelete = async (id) => {
        if (!window.confirm('¿Eliminar este tipo de menú?')) return;
        try {
            await axios.delete(`${apiBaseUrl}/tipos-menu/${id}`);
            loadTipos();
        } catch (err) {
            console.error('Error al eliminar tipo de menú', err);
            setError('No se pudo eliminar el tipo de menú');
        }
    };

    return (
        <div className={`min-h-screen p-3 lg:p-4 font-sans overflow-x-hidden transition-colors duration-300 ${isDarkMode ? "bg-slate-950 text-white" : "bg-[#f8f5e9] text-gray-800"}`}>

            {/* HEADER CARD - COMPACT */}
            <div className={`${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-gray-100"} rounded-xl shadow-sm p-4 mb-4 flex flex-col md:flex-row items-center justify-between border animate-in slide-in-from-top duration-700`}>
                <div className="flex items-center gap-3">
                    <div className="bg-gradient-to-br from-orange-500 to-orange-700 p-2.5 rounded-lg shadow-lg">
                        <CategoryIcon className="text-white" sx={{ fontSize: 24 }} />
                    </div>
                    <div>
                        <h1 className={`text-lg font-black tracking-tight ${isDarkMode ? "text-white" : "text-gray-800"}`}>Configuración de Categorías</h1>
                        <p className={`text-[10px] ${isDarkMode ? "text-slate-400" : "text-gray-500"} font-black uppercase tracking-wider`}>Administra los tipos de menú y visualiza sus platos asociados</p>
                    </div>
                </div>
                {editingId && (
                    <button
                        onClick={resetForm}
                        className="mt-3 md:mt-0 px-3 py-1.5 bg-slate-500 hover:bg-slate-600 text-white rounded-lg text-[11px] font-black uppercase tracking-widest transition-all shadow-sm"
                    >
                        Cancelar Edición
                    </button>
                )}
            </div>

            {error && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 text-red-500 rounded-lg text-xs font-bold animate-shake">
                    {error}
                </div>
            )}

            <div className="flex flex-col lg:flex-row gap-4">

                {/* SIDEBAR - LIST OF CATEGORIES */}
                <div className="w-full lg:w-64 flex-shrink-0 animate-in slide-in-from-left duration-700">
                    <div className="bg-orange-600 rounded-t-lg p-2.5 shadow-md">
                        <h3 className="text-white font-black text-[10px] uppercase tracking-[0.15em]">Categorías Disponibles</h3>
                    </div>
                    <div className="flex flex-col gap-1.5 pt-1">
                        {tipos.map((tipo) => {
                            const isSelected = selectedTipo?.id === tipo.id;
                            const isActivo = tipo.activo === undefined || tipo.activo === null ? true : !!tipo.activo;
                            return (
                                <div key={tipo.id} className="relative group">
                                    <button
                                        onClick={() => fetchPlatosByTipo(tipo)}
                                        className={`w-full flex flex-col items-start px-3 py-2.5 rounded-lg transition-all border text-left ${isSelected
                                            ? "bg-orange-600 text-white border-orange-500 shadow-lg scale-[1.02] font-black"
                                            : isDarkMode
                                                ? "bg-slate-900 text-slate-400 border-slate-800 hover:border-orange-500/50 hover:bg-orange-500/10"
                                                : "bg-white text-gray-600 border-gray-100 hover:border-orange-200 hover:bg-orange-50 shadow-sm"
                                            }`}
                                    >
                                        <div className="flex items-center justify-between w-full mb-0.5">
                                            <span className="truncate text-xs pr-6 uppercase tracking-tight">{tipo.nombre}</span>
                                            <div className={`w-1.5 h-1.5 rounded-full ${isActivo ? 'bg-green-500' : 'bg-slate-500'}`}></div>
                                        </div>
                                        <span className={`text-[9px] truncate w-full font-bold italic ${isSelected ? "text-orange-100" : "text-slate-500"}`}>
                                            {tipo.descripcion || 'Sin descripción'}
                                        </span>
                                    </button>

                                    {/* Quick Actions for categories */}
                                    <div className={`absolute right-2 top-2 hidden group-hover:flex gap-1 ${isSelected ? "text-white" : ""}`}>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleEdit(tipo); }}
                                            className="p-1 hover:bg-black/10 rounded transition-colors"
                                        >
                                            <EditIcon sx={{ fontSize: 13 }} />
                                        </button>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleDelete(tipo.id); }}
                                            className="p-1 hover:bg-red-500/20 rounded text-red-500 transition-colors"
                                        >
                                            <DeleteIcon sx={{ fontSize: 13 }} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}

                        {tipos.length === 0 && !loading && (
                            <div className={`p-4 text-center text-[10px] italic font-bold uppercase tracking-widest ${isDarkMode ? "text-slate-700" : "text-gray-400"}`}>
                                No hay categorías
                            </div>
                        )}
                        {loading && (
                            <div className="flex justify-center p-4">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500"></div>
                            </div>
                        )}
                    </div>
                </div>

                {/* MAIN CONTENT AREA */}
                <div className="flex-1 min-w-0 space-y-4 animate-in slide-in-from-bottom duration-1000">

                    {/* FORM CARD */}
                    <div className={`${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-gray-100"} rounded-xl shadow-sm border p-4`}>
                        <h3 className={`text-[10px] font-black mb-3 uppercase tracking-[0.2em] ${isDarkMode ? "text-slate-500" : "text-gray-400"}`}>
                            {editingId ? "Editar Categoría" : "Nueva Categoría"}
                        </h3>
                        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                            <div className="md:col-span-4">
                                <label className="block text-[10px] font-bold mb-1 uppercase text-slate-500">Nombre</label>
                                <input
                                    name="nombre"
                                    value={form.nombre}
                                    onChange={handleChange}
                                    placeholder="Ej: Menú Semanal"
                                    className={`w-full px-3 py-2 rounded-lg text-xs transition-all border focus:outline-none focus:ring-1 focus:ring-orange-500/30 ${isDarkMode
                                        ? "bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600"
                                        : "bg-gray-50 border-gray-200 text-gray-700"
                                        }`}
                                    required
                                />
                            </div>
                            <div className="md:col-span-5">
                                <label className="block text-[10px] font-bold mb-1 uppercase text-slate-500">Descripción</label>
                                <input
                                    name="descripcion"
                                    value={form.descripcion}
                                    onChange={handleChange}
                                    placeholder="Descripción corta de la categoría..."
                                    className={`w-full px-3 py-2 rounded-lg text-xs transition-all border focus:outline-none focus:ring-1 focus:ring-orange-500/30 ${isDarkMode
                                        ? "bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600"
                                        : "bg-gray-50 border-gray-200 text-gray-700"
                                        }`}
                                />
                            </div>
                            <div className="md:col-span-3 flex gap-2">
                                <button
                                    type="submit"
                                    className="flex-1 bg-orange-600 hover:bg-orange-700 text-white rounded-lg px-4 py-2 flex items-center justify-center gap-2 text-xs font-bold transition-all shadow-sm"
                                >
                                    <AddIcon sx={{ fontSize: 16 }} />
                                    {editingId ? 'Actualizar' : 'Crear'}
                                </button>
                                {editingId && (
                                    <button
                                        type="button"
                                        onClick={resetForm}
                                        className={`px-3 py-2 rounded-lg border transition-all ${isDarkMode ? "border-slate-800 text-slate-400 hover:bg-slate-800" : "border-gray-200 text-gray-500 hover:bg-gray-50"
                                            }`}
                                    >
                                        Limpiar
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>

                    {/* PLATE LIST CARD */}
                    {selectedTipo ? (
                        <div className={`${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-gray-100"} rounded-xl shadow-sm border overflow-hidden`}>
                            <div className="bg-slate-800 p-3 flex items-center justify-between">
                                <div>
                                    <h2 className="text-sm font-bold text-white leading-tight">
                                        Platos en <span className="text-orange-500">{selectedTipo.nombre}</span>
                                    </h2>
                                    <p className="text-slate-400 text-[10px] font-medium">{platosDelTipo.length} platos asociados</p>
                                </div>
                                <RestaurantMenuIcon className="text-slate-600" sx={{ fontSize: 20 }} />
                            </div>

                            <div className="overflow-x-auto min-h-[200px]">
                                {loadingPlatos ? (
                                    <div className="flex flex-col items-center justify-center py-12 gap-3">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                                        <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Cargando platos...</span>
                                    </div>
                                ) : platosDelTipo.length > 0 ? (
                                    <table className="min-w-full text-xs">
                                        <thead>
                                            <tr className={`${isDarkMode ? "bg-slate-950 text-slate-400" : "bg-gray-50 text-gray-400"} text-[9px] uppercase font-bold tracking-wider`}>
                                                <th className="px-4 py-2 text-left">Nombre del Plato</th>
                                                <th className="px-4 py-2 text-left">Descripción</th>
                                            </tr>
                                        </thead>
                                        <tbody className={`divide-y ${isDarkMode ? "divide-slate-800" : "divide-gray-50"}`}>
                                            {platosDelTipo.map((p, idx) => (
                                                <tr key={p.id_plato} className={`${isDarkMode ? "hover:bg-slate-800/30" : "hover:bg-orange-50/10"} transition-colors ${idx % 2 === 0 ? '' : (isDarkMode ? 'bg-slate-950/30' : 'bg-gray-50/5')}`}>
                                                    <td className="px-4 py-2.5 font-black tracking-tight" style={{ color: isDarkMode ? '#818cf8' : '#312e81' }}>{p.nombre}</td>
                                                    <td className={`px-4 py-2.5 italic text-[11px] font-medium ${isDarkMode ? "text-slate-300" : "text-gray-500"}`}>
                                                        {p.descripcion || '-'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-12 gap-2 text-center border-t border-slate-800/50">
                                        <div className="p-3 bg-slate-800/50 rounded-full">
                                            <RestaurantMenuIcon className="text-slate-600" sx={{ fontSize: 24 }} />
                                        </div>
                                        <p className="text-xs text-slate-500 font-medium italic">No hay platos asociados a esta categoría.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className={`h-64 rounded-xl flex flex-col items-center justify-center text-center border border-dashed p-6 ${isDarkMode ? "bg-slate-900/30 border-slate-800 text-slate-600" : "bg-white border-gray-200 text-gray-400"
                            }`}>
                            <CategoryIcon className="opacity-10 mb-3" sx={{ fontSize: 48 }} />
                            <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-1">Selecciona una Categoría</h3>
                            <p className="text-[10px] max-w-[200px]">Haz clic en una categoría de la izquierda para ver y gestionar sus platos.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AbmTiposMenu;
